import { COLORS } from '@constants/colors';

export const useStyles = () => ({
  paper: {
    borderRadius: '12px',
    background:
      'linear-gradient(135deg, rgba(35, 38, 47, 0.98) 0%, rgba(18, 18, 22, 0.98) 100%)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(12px)',
    color: COLORS.WHITE,
    position: 'relative' as const,
    overflow: 'hidden',
  },
  title: {
    fontWeight: 600,
    color: COLORS.WHITE,
    padding: '18px 20px 14px',
    paddingRight: '56px',
    fontSize: 16,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '0 20px 16px',
  },
  dropZone: {
    border: '1px dashed rgba(255, 255, 255, 0.16)',
    borderRadius: '10px',
    padding: '28px 20px',
    textAlign: 'center' as const,
    backgroundColor: 'rgba(25, 25, 30, 0.55)',
    minHeight: 260,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  dropZoneActive: {
    borderColor: COLORS.MYTAVERSE_BLUE,
    background: 'rgba(69, 84, 229, 0.10)',
  },
  previewZone: {
    padding: 0,
    cursor: 'default',
    overflow: 'hidden',
    borderStyle: 'solid',
    minHeight: 'unset',
    alignItems: 'stretch',
    justifyContent: 'stretch',
  },
  previewFrame: {
    width: '100%',
    maxWidth: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.10)',
  },
  getPreviewFrame: (targetAspect: number) => ({
    width: '100%',
    maxWidth: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.10)',
    aspectRatio: `${targetAspect}`,
  }),
  dropZoneTitle: {
    color: COLORS.WHITE,
    marginTop: '10px',
    fontSize: 14,
    fontWeight: 500,
  },
  dropZoneSubtitle: {
    color: 'rgba(255, 255, 255, 0.65)',
    marginTop: '6px',
    fontSize: 12,
  },
  uploadIcon: {
    fontSize: 40,
    color: COLORS.MYTAVERSE_BLUE,
  },
  linkButton: {
    marginTop: '8px',
    textTransform: 'none' as const,
    fontWeight: 500,
    fontSize: 12,
    color: COLORS.MYTAVERSE_BLUE,
    padding: 0,
    minHeight: 'auto',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  previewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'fill' as const,
    display: 'block',
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statusText: {
    color: 'rgba(255, 255, 255, 0.85)',
    fontSize: 13,
  },
  errorText: {
    color: COLORS.RED,
    fontSize: 13,
  },
  closeButton: {
    position: 'absolute' as const,
    top: 12,
    right: 12,
    color: 'rgba(255, 255, 255, 0.65)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },

  requirementsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '10px 4px 0',
    flexWrap: 'wrap' as const,
  },
  requirementsLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    minWidth: 220,
  },
  requirementsText: {
    color: 'rgba(255, 255, 255, 0.60)',
    fontSize: 12,
  },
  requirementsStrong: {
    color: COLORS.WHITE,
    fontWeight: 500,
  },
  copyButton: {
    width: 22,
    height: 22,
    color: 'rgba(255, 255, 255, 0.65)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
    '& .MuiSvgIcon-root': {
      fontSize: 16,
    },
  },

  footer: {
    padding: '12px 20px 16px',
    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
    backgroundColor: 'rgba(0, 0, 0, 0.18)',
  },
  footerSingleButton: {
    width: '100%',
    textTransform: 'none' as const,
    borderRadius: '10px',
    padding: '12px 16px',
    color: COLORS.WHITE,
    borderColor: 'rgba(255, 255, 255, 0.20)',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.32)',
      backgroundColor: 'rgba(255, 255, 255, 0.10)',
    },
  },
  footerButtons: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap' as const,
  },
  primaryButton: {
    flex: 1,
    minWidth: 160,
    textTransform: 'none' as const,
    borderRadius: '10px',
    padding: '12px 16px',
    fontWeight: 600,
    backgroundColor: COLORS.MYTAVERSE_BLUE,
    '&:hover': {
      backgroundColor: '#3A46C1',
    },
    '&.Mui-disabled': {
      backgroundColor: 'rgba(69, 84, 229, 0.40)',
      color: 'rgba(255, 255, 255, 0.55)',
    },
  },
  secondaryButton: {
    flex: 1,
    minWidth: 160,
    textTransform: 'none' as const,
    borderRadius: '10px',
    padding: '12px 16px',
    color: COLORS.WHITE,
    borderColor: 'rgba(255, 255, 255, 0.20)',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.32)',
      backgroundColor: 'rgba(255, 255, 255, 0.10)',
    },
  },
  tertiaryButton: {
    flex: 1,
    minWidth: 140,
    textTransform: 'none' as const,
    borderRadius: '10px',
    padding: '12px 16px',
    color: 'rgba(255, 255, 255, 0.90)',
    borderColor: 'rgba(255, 255, 255, 0.18)',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.30)',
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  failurePaper: {
    borderRadius: '12px',
    background:
      'linear-gradient(135deg, rgba(35, 38, 47, 0.98) 0%, rgba(18, 18, 22, 0.98) 100%)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(12px)',
    color: COLORS.WHITE,
    position: 'relative' as const,
    overflow: 'hidden',
  },
  failureTitle: {
    fontWeight: 600,
    color: COLORS.WHITE,
    padding: '16px 18px 10px',
    paddingRight: '52px',
    fontSize: 16,
  },
  failureCloseButton: {
    position: 'absolute' as const,
    top: 10,
    right: 10,
    color: 'rgba(255, 255, 255, 0.65)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  failureContent: {
    padding: '0 18px 16px',
  },
  failureText: {
    color: 'rgba(255, 255, 255, 0.75)',
    fontSize: 13,
    lineHeight: 1.4,
  },
  failureDetails: {
    marginTop: '10px',
    color: 'rgba(255, 255, 255, 0.65)',
    fontSize: 12,
  },
  failureActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '14px',
    flexWrap: 'wrap' as const,
  },
  failureMoreInfoButton: {
    textTransform: 'none' as const,
    borderRadius: '10px',
    padding: '8px 14px',
    color: COLORS.WHITE,
    borderColor: 'rgba(255, 255, 255, 0.20)',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.32)',
      backgroundColor: 'rgba(255, 255, 255, 0.10)',
    },
  },
  failureTryAgainButton: {
    textTransform: 'none' as const,
    borderRadius: '10px',
    padding: '8px 14px',
    fontWeight: 600,
    backgroundColor: COLORS.MYTAVERSE_BLUE,
    '&:hover': {
      backgroundColor: '#3A46C1',
    },
  },
  videoMediaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '12px',
    alignItems: 'stretch',
    '@media (max-width: 760px)': {
      gridTemplateColumns: '1fr',
    },
  },
  videoMediaCard: {
    border: '1px dashed rgba(255, 255, 255, 0.16)',
    borderRadius: '10px',
    padding: '18px',
    textAlign: 'center' as const,
    backgroundColor: 'rgba(25, 25, 30, 0.55)',
    height: 230,
    minHeight: 230,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    overflow: 'hidden',
    transition: 'all 0.2s ease',
  },
  videoMediaCardSelected: {
    padding: 0,
    borderStyle: 'solid',
    cursor: 'default',
    backgroundColor: 'rgba(0, 0, 0, 0.20)',
  },
  videoMediaLabel: {
    color: 'rgba(255, 255, 255, 0.65)',
    fontSize: 12,
    fontWeight: 500,
    marginBottom: '10px',
  },
  mediaUploadPromptWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoPreviewCardContent: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: 230,
  },
  videoPreviewPlayer: {
    width: '100%',
    flex: 1,
    minHeight: 174,
    maxHeight: 260,
    backgroundColor: COLORS.BLACK,
    display: 'block',
    objectFit: 'contain' as const,
  },
  mediaCardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    padding: '10px 12px',
    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
    backgroundColor: 'rgba(0, 0, 0, 0.24)',
    width: '100%',
  },
  fileSelectedZone: {
    minHeight: 'unset',
    padding: '16px 20px',
    cursor: 'default',
  },
  selectedFileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    justifyContent: 'center',
  },
  selectedFileName: {
    color: COLORS.WHITE,
    fontSize: 13,
    fontWeight: 500,
    wordBreak: 'break-all' as const,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  previewImageSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: 230,
    minHeight: 230,
  },
  previewImageSectionLabel: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.60)',
    fontWeight: 500,
  },
  requiredMark: {
    color: '#F87171',
    fontWeight: 600,
  },
  previewImageDropZone: {
    border: '1px dashed rgba(255, 255, 255, 0.16)',
    borderRadius: '10px',
    padding: '16px 20px',
    textAlign: 'center' as const,
    backgroundColor: 'rgba(25, 25, 30, 0.55)',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flex: 1,
    height: '100%',
    minHeight: 230,
    gap: '4px',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.28)',
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
    },
  },
  previewImageUploadIcon: {
    fontSize: 28,
    color: COLORS.MYTAVERSE_BLUE,
    marginBottom: '2px',
  },
  previewImageDropText: {
    color: COLORS.WHITE,
    fontSize: 12,
    fontWeight: 500,
  },
  previewImageDropHint: {
    color: 'rgba(255, 255, 255, 0.45)',
    fontSize: 11,
  },
  previewImageThumbWrapper: {
    position: 'relative' as const,
    width: '100%',
    flex: 1,
    height: '100%',
    minHeight: 230,
    borderRadius: '10px',
    overflow: 'hidden',
    cursor: 'pointer',
    backgroundColor: 'rgba(0, 0, 0, 0.20)',
    '&:hover .preview-image-overlay': {
      opacity: 1,
    },
  },
  previewImageThumb: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    display: 'block',
  },
  previewImageOverlay: {
    position: 'absolute' as const,
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLORS.WHITE,
    opacity: 0,
    transition: 'opacity 0.18s ease',
    fontSize: 13,
  },
  previewImageOverlayIcon: {
    fontSize: 20,
    marginRight: '4px',
  },
  previewImageOverlayText: {
    fontSize: 12,
  },
  progressWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  progressPercent: {
    color: 'rgba(255, 255, 255, 0.75)',
    fontSize: 12,
    fontWeight: 500,
    marginLeft: 'auto',
  },
  linearProgress: {
    borderRadius: '4px',
    height: 5,
    backgroundColor: 'rgba(255, 255, 255, 0.10)',
    '& .MuiLinearProgress-bar': {
      backgroundColor: COLORS.MYTAVERSE_BLUE,
      borderRadius: '4px',
    },
  },
  assetUpdatedPaper: {
    borderRadius: '12px',
    background:
      'linear-gradient(135deg, rgba(35, 38, 47, 0.98) 0%, rgba(18, 18, 22, 0.98) 100%)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(12px)',
    color: COLORS.WHITE,
    position: 'relative' as const,
    overflow: 'hidden',
  },
  assetUpdatedTitle: {
    fontWeight: 600,
    color: COLORS.WHITE,
    padding: '18px 20px 10px',
    paddingRight: '56px',
    fontSize: 18,
  },
  assetUpdatedCloseButton: {
    position: 'absolute' as const,
    top: 12,
    right: 12,
    color: 'rgba(255, 255, 255, 0.65)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  assetUpdatedContent: {
    padding: '0 20px 18px',
  },
  assetUpdatedText: {
    color: 'rgba(255, 255, 255, 0.75)',
    fontSize: 14,
    lineHeight: 1.4,
    maxWidth: 520,
  },
  assetUpdatedActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '16px',
  },
  assetUpdatedRevertButton: {
    textTransform: 'none' as const,
    borderRadius: '10px',
    padding: '8px 18px',
    color: COLORS.WHITE,
    borderColor: 'rgba(255, 255, 255, 0.28)',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.40)',
      backgroundColor: 'rgba(255, 255, 255, 0.04)',
    },
  },
  assetUpdatedSaveButton: {
    textTransform: 'none' as const,
    borderRadius: '10px',
    padding: '8px 18px',
    fontWeight: 600,
    backgroundColor: COLORS.MYTAVERSE_BLUE,
    '&:hover': {
      backgroundColor: '#3A46C1',
    },
  },
});
